import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { saveWorkingProjectByFields, saveWorkingProjectSessionByField } from '@/store/slices/project';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { getDefaultLanguage } from '@/utils/get-default-language';
import EditDescription from '../edit-description/edit-description';
import extractTextFromHtmlString from '@/utils/extract-text-from-html-string';
import { PageModule, PageModuleType } from '@/types/module.types';

type EditSessionDescriptionProps = {
  sessionUuid?: string;
  syncProjectDescription?: boolean;
};

const EditSessionDescription = ({ sessionUuid, syncProjectDescription }: EditSessionDescriptionProps) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const workingProject = useAppSelector(getWorkingProject);
  const baseLanguage = getDefaultLanguage(workingProject);
  const language = params?.language || baseLanguage;
  const sessions = workingProject?.sessions;
  const isMultiSession = Array.isArray(sessions) && sessions.length > 1;
  const session = sessions?.find((s) => s.uuid === sessionUuid);

  /*
    If we want to use the original module session description that uses Quill we should use this module value.
    However, it should also update the module value when the session description is updated.
    It should also use quill instead of TextAreaAutosize. But, product decided to replace the session page
    description with plain text, so we will be using plain text going forward.
    Just leaving this here for reference.
    */
  // const sessionDescriptionModule = session?.modules?.find((m) => m.type === PageModuleType.description);

  const handleBlur = (value: string) => {
    if (!session) return;

    if (value === session.description?.[language]) {
      return;
    }

    const updatedDescription = updateTranslateKey({
      translateString: session.description ?? '',
      input: value,
      language,
      baseLanguage,
    });

    dispatch(
      saveWorkingProjectSessionByField({
        uuid: session.uuid,
        description: updatedDescription,
        // update description module to ensure attendee page is updated on publish
        modules: session.modules.map((m) => {
          if (m.type === PageModuleType.description) {
            const _page_module: PageModule = {
              ...m,
              content: {
                ...m.content,
                description: updatedDescription,
              },
              is_edited: true,
            };
            return _page_module;
          }
          return m;
        }),
      })
    );

    if (syncProjectDescription) {
      dispatch(saveWorkingProjectByFields({ event_description: value }));
    }
  };

  const description = useMemo(() => {
    const descriptionModuleContent = session?.modules?.find((m) => m.type === PageModuleType.description)?.content
      ?.description;

    return (
      descriptionModuleContent?.[language] ??
      descriptionModuleContent?.base ??
      session?.description?.[language] ??
      session?.description?.base ??
      (!isMultiSession ? workingProject?.event_description : '') ??
      ''
    );
  }, [isMultiSession, language, session?.description, session?.modules, workingProject?.event_description]);

  if (!session) return null;
  return (
    <EditDescription
      // Note: session description was originally quill, but we are no longer making them quill and they are now plain text
      defaultValue={extractTextFromHtmlString(Array.isArray(description) ? description[0] : description) || ''}
      onBlur={handleBlur}
      maxRowsFocused={3}
    />
  );
};

export default EditSessionDescription;
